/**
 * AdminIntegracoesConsolidatedPage — Consolidated Integrations (API Keys + Integrações)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, Building2, ChevronDown } from 'lucide-react';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { AdminApiKeysPage } from './AdminApiKeysPage';
import { AdminIntegrationsPage } from './AdminIntegrationsPage';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';

export const AdminIntegracoesConsolidatedPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'integracoes';
  const [search, setSearch] = useState('');
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  useEffect(() => {
    AdminConsoleService.getOrganizations().then((list) => {
      setOrgs(list);
      if (list.length > 0) setSelectedOrgId(list[0].id);
    });
  }, []);

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const filtersWidget = useMemo(() => {
    if (activeTab === 'api-keys') {
      return (
        <div className="flex flex-1 items-center justify-end gap-1.5">
          <div className="flex w-[220px] shrink-0 items-center">
            <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
              <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
              <input
                type="text"
                placeholder="Buscar chave ou org..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
              />
              {search && (
                <button onClick={() => setSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
                  <X size={10} className="text-[var(--text-secondary)]" />
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (activeTab === 'integracoes') {
      return (
        <div className="flex flex-1 items-center justify-end gap-1.5">
          <div className="relative">
            <select
              className={`${filterBtnClass} appearance-none cursor-pointer`}
              style={{ paddingLeft: 22, paddingRight: 22 }}
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
            >
              <option value="">Selecionar cliente...</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <Building2 size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
        </div>
      );
    }
    return null;
  }, [activeTab, search, selectedOrgId, orgs, filterBtnClass]);

  return (
    <AdminConsoleLayout ribbonSlots={{ afterGroups: filtersWidget }}>
      <div className="h-full overflow-auto">
        {activeTab === 'integracoes' && <AdminIntegrationsPage externalSelectedOrg={selectedOrgId} />}
        {activeTab === 'api-keys' && <AdminApiKeysPage externalSearch={search} />}
      </div>
    </AdminConsoleLayout>
  );
};
